import { treasurerEmailPrintLine } from "../../printSafety";
import { updateDoc } from "../../auditedFirestore";
import { recordActivity } from "../../auditActivity";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { db } from "../../firebase";
import SearchableSelect from "../../SearchableSelect";
import { doc, serverTimestamp } from "firebase/firestore";

const emptyPlan = () => ({
  version: 2,
  room: { width: 1200, height: 800, name: "Main Room" },
  tables: [],
  notes: "",
  updatedAt: null,
});

const genId = (prefix = "id") => `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
const fmtDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};
const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

function deepClone(value) {
  return JSON.parse(JSON.stringify(value));
}

function defaultSeatPlacement(shape, index, count) {
  if (shape === "round") {
    return { angle: -90 + (360 * index) / Math.max(1, count) };
  }
  return { t: index / Math.max(1, count) };
}

function normalisePlan(raw) {
  const base = emptyPlan();
  const plan = raw && typeof raw === "object" ? deepClone(raw) : base;
  plan.version = 2;
  plan.room = {
    width: Number(plan?.room?.width) || 1200,
    height: Number(plan?.room?.height) || 800,
    name: plan?.room?.name || "Main Room",
  };
  plan.notes = plan?.notes || "";
  plan.tables = Array.isArray(plan?.tables) ? plan.tables.map((t, i) => normaliseTable(t, i)) : [];
  return plan;
}

function normaliseTable(raw, index = 0) {
  const shape = raw?.shape === "round" ? "round" : "rect";
  const seatCount = clamp(Number(raw?.seatCount) || (shape === "round" ? 8 : 6), 1, 24);
  const seats = Array.from({ length: seatCount }, (_, i) => ({
    id: raw?.seats?.[i]?.id || genId("seat"),
    name: raw?.seats?.[i]?.name || "",
    ...(shape === "round"
      ? { angle: Number.isFinite(Number(raw?.seats?.[i]?.angle)) ? Number(raw.seats[i].angle) : defaultSeatPlacement(shape, i, seatCount).angle }
      : { t: Number.isFinite(Number(raw?.seats?.[i]?.t)) ? clamp(Number(raw.seats[i].t), 0, 0.9999) : defaultSeatPlacement(shape, i, seatCount).t }),
  }));
  return {
    id: raw?.id || genId("table"),
    label: raw?.label || `Table ${index + 1}`,
    shape,
    x: Number(raw?.x) || 120 + index * 40,
    y: Number(raw?.y) || 120 + index * 30,
    width: clamp(Number(raw?.width) || 220, 80, 1000),
    height: clamp(Number(raw?.height) || (shape === "round" ? 220 : 120), 80, 1000),
    seatCount,
    seats,
  };
}

function resizeSeats(table, nextSeatCount, nextShape = table.shape) {
  const count = clamp(Number(nextSeatCount) || 1, 1, 24);
  const seats = Array.from({ length: count }, (_, i) => {
    const existing = table.seats?.[i] || {};
    const fallback = defaultSeatPlacement(nextShape, i, count);
    return {
      id: existing.id || genId("seat"),
      name: existing.name || "",
      ...(nextShape === "round"
        ? { angle: Number.isFinite(Number(existing.angle)) ? Number(existing.angle) : fallback.angle }
        : { t: Number.isFinite(Number(existing.t)) ? clamp(Number(existing.t), 0, 0.9999) : fallback.t }),
    };
  });
  return { ...table, shape: nextShape, seatCount: count, seats };
}

function seatPoint(table, seat) {
  const w = Number(table.width) || 220;
  const h = Number(table.height) || 120;

  if (table.shape === "round") {
    const radius = Math.max(w, h) / 2;
    const angle = ((Number(seat?.angle) || 0) * Math.PI) / 180;
    return {
      left: w / 2 + Math.cos(angle) * (radius + 34),
      top: h / 2 + Math.sin(angle) * (radius + 34),
    };
  }

  const t = clamp(Number(seat?.t) || 0, 0, 0.9999);
  const perim = 2 * (w + h);
  const d = t * perim;
  if (d < w) return { left: d, top: -28 };
  if (d < w + h) return { left: w + 28, top: d - w };
  if (d < 2 * w + h) return { left: w - (d - (w + h)), top: h + 28 };
  return { left: -28, top: h - (d - (2 * w + h)) };
}


function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function planDocumentHtml(plan, booking, lodgeName, logo, treasurerEmails = []) {
  const headingName = escapeHtml(booking?.lodgeName || lodgeName || "Lodge");
  const safeTitle = headingName;
  const bookingMeta = escapeHtml(`${booking?.lodgeName || lodgeName || ""} • ${fmtDate(booking?.date)} ${booking?.time || ""} • ${booking?.diners || 0} covers`);
  const notes = plan?.notes ? `<div class="notes"><strong>Notes:</strong> ${escapeHtml(plan.notes)}</div>` : "";
  const tablesHtml = (plan?.tables || []).map((table) => {
    const tableStyle = [
      `left:${Number(table.x) || 0}px`,
      `top:${Number(table.y) || 0}px`,
      `width:${Number(table.width) || 220}px`,
      `height:${Number(table.height) || 120}px`
    ].join(';');
    const shapeClass = table.shape === 'round' ? 'table round' : 'table rect';
    const seatsHtml = (table.seats || []).map((seat, i) => {
      const pos = seatPoint(table, seat);
      return `<div class="seat" style="left:${pos.left}px;top:${pos.top}px">${escapeHtml(seat.name || `Seat ${i+1}`)}</div>`;
    }).join('');
    return `<div class="table-wrap" style="${tableStyle}"><div class="${shapeClass}"><div class="table-label">${escapeHtml(table.label)}</div><div class="table-meta">${table.shape === 'round' ? 'Round' : 'Rectangular'} • ${Number(table.seatCount) || 0} seats</div></div>${seatsHtml}</div>`;
  }).join('');

  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${safeTitle}</title>
<style>
  *{box-sizing:border-box} body{font-family:Arial,sans-serif;margin:24px;color:#111} .hdr{display:flex;align-items:center;gap:14px;margin-bottom:6px} .logo{height:52px;width:auto;max-width:120px;object-fit:contain} h1{margin:0;font-size:28px} .meta{color:#555;margin-bottom:16px} .board{position:relative;width:${plan?.room?.width || 1200}px;height:${plan?.room?.height || 800}px;border:2px solid #999;border-radius:16px;background:#fafafa;overflow:hidden} .watermark{position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);width:62%;height:62%;object-fit:contain;opacity:0.08;pointer-events:none;z-index:0} .table-wrap{position:absolute;overflow:visible;z-index:1} .table{position:absolute;inset:0;border:1px solid #666;background:#fff;display:grid;place-items:center;text-align:center;padding:8px} .table.round{border-radius:999px} .table.rect{border-radius:12px} .table-label{font-weight:700;font-size:16px} .table-meta{font-size:11px;color:#555;margin-top:4px} .seat{position:absolute;transform:translate(-50%,-50%);min-width:62px;max-width:120px;padding:5px 7px;border:1px solid #777;border-radius:999px;background:#fff;text-align:center;font-size:11px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis} .notes{margin-top:16px;font-size:13px} @media print{body{margin:10mm}.board{break-inside:avoid-page}}
</style>
</head>
<body>
  <div class="hdr">${logo ? `<img class="logo" src="${escapeHtml(logo)}" alt="" />` : ""}<h1>${headingName}</h1></div>
  <div class="meta">${bookingMeta}</div>
  ${treasurerEmailPrintLine(treasurerEmails)}
  <div class="board">${logo ? `<img class="watermark" src="${escapeHtml(logo)}" alt="" />` : ""}${tablesHtml}</div>
  ${notes}
</body>
</html>`;
}

function card(T) {
  return { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 18 };
}

function btn(T) {
  return { padding: "10px 14px", borderRadius: 9, border: "none", background: T.primary || T.accent, color: "#fff", fontWeight: 700, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" };
}

function btn2(T) {
  return { padding: "10px 14px", borderRadius: 9, border: `1px solid ${T.borderInput || T.border}`, background: T.surface, color: T.primary || T.accent, fontWeight: 700, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" };
}

function input(T) {
  return { width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${T.borderInput || T.border}`, background: T.surface, color: T.text, fontFamily: "'DM Sans',sans-serif" };
}

function label(T) {
  return { display: "block", fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: T.textMuted, marginBottom: 6 };
}

function Board({ T, plan, selectedId, setSelectedId, updateTablePosition, updateSeatPlacement, updateSeatName }) {
  const boardRef = useRef(null);
  const dragRef = useRef(null);

  useEffect(() => {
    const move = (e) => {
      if (!dragRef.current || !boardRef.current) return;
      const rect = boardRef.current.getBoundingClientRect();
      const drag = dragRef.current;

      if (drag.kind === "table") {
        const x = e.clientX - rect.left - drag.offsetX;
        const y = e.clientY - rect.top - drag.offsetY;
        updateTablePosition(drag.tableId, x, y);
        return;
      }

      if (drag.kind === "seat") {
        const x = e.clientX - rect.left - drag.tableX;
        const y = e.clientY - rect.top - drag.tableY;
        updateSeatPlacement(drag.tableId, drag.seatId, x, y);
      }
    };
    const up = () => { dragRef.current = null; };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
  }, [updateSeatPlacement, updateTablePosition]);

  return (
    <div style={{ ...card(T), overflow: "auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 12 }}>
        <div>
          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 20 }}>Planner Board</div>
          <div style={{ fontSize: 12, color: T.textMuted }}>{plan.room.name} · {plan.room.width} × {plan.room.height}</div>
        </div>
        <div style={{ fontSize: 12, color: T.textMuted }}>Drag tables or drag seats around the table edge</div>
      </div>

      <div
        ref={boardRef}
        style={{
          position: "relative",
          width: plan.room.width,
          height: plan.room.height,
          minWidth: "100%",
          background: `linear-gradient(180deg, ${T.surfaceAlt}, ${T.bg})`,
          border: `1px dashed ${T.border}`,
          borderRadius: 14,
          overflow: "hidden",
        }}
      >
        {plan.tables.length === 0 && (
          <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", color: T.textMuted, fontSize: 14 }}>
            Add a table to begin the seating plan.
          </div>
        )}

        {plan.tables.map((table) => {
          const selected = table.id === selectedId;
          return (
            <div
              key={table.id}
              style={{ position: "absolute", left: table.x, top: table.y, width: table.width, height: table.height, overflow: "visible" }}
              onMouseDown={(e) => {
                if (e.target.closest("input,textarea,button,select,[data-seat-pill='true']")) return;
                const box = e.currentTarget.getBoundingClientRect();
                dragRef.current = { kind: "table", tableId: table.id, offsetX: e.clientX - box.left, offsetY: e.clientY - box.top };
                setSelectedId(table.id);
              }}
              onClick={() => setSelectedId(table.id)}
            >
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  borderRadius: table.shape === "round" ? "999px" : 12,
                  background: selected ? T.primaryLight || `${T.accent}18` : T.surface,
                  border: selected ? `2px solid ${T.primary || T.accent}` : `1px solid ${T.border}`,
                  display: "grid",
                  placeItems: "center",
                  boxShadow: selected ? "0 10px 20px rgba(0,0,0,0.08)" : "none",
                  cursor: "grab",
                  userSelect: "none",
                }}
              >
                <div style={{ textAlign: "center", pointerEvents: "none" }}>
                  <div style={{ fontWeight: 800, color: T.text }}>{table.label}</div>
                  <div style={{ fontSize: 11, color: T.textMuted }}>{table.shape === "round" ? "Round" : "Rectangular"} · {table.seatCount} seats</div>
                </div>
              </div>

              {table.seats.map((seat, i) => {
                const pos = seatPoint(table, seat);
                return (
                  <div
                    key={seat.id || i}
                    data-seat-pill="true"
                    style={{
                      position: "absolute",
                      left: pos.left,
                      top: pos.top,
                      transform: "translate(-50%, -50%)",
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      minWidth: 44,
                      maxWidth: 104,
                      padding: "2px 5px",
                      borderRadius: 999,
                      background: T.bg,
                      border: `1px solid ${selected ? (T.primary || T.accent) : T.border}`,
                    }}
                    title={seat.name || `Seat ${i + 1}`}
                  >
                    <span
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        dragRef.current = { kind: "seat", tableId: table.id, seatId: seat.id, tableX: table.x, tableY: table.y };
                        setSelectedId(table.id);
                      }}
                      title="Drag to reposition"
                      style={{ cursor: "grab", color: T.textMuted, fontSize: 10, lineHeight: 1, userSelect: "none", flexShrink: 0 }}
                    >
                      &#10287;
                    </span>
                    <input
                      value={seat.name || ""}
                      placeholder={`Seat ${i + 1}`}
                      onMouseDown={(e) => e.stopPropagation()}
                      onFocus={() => setSelectedId(table.id)}
                      onChange={(e) => updateSeatName(table.id, seat.id, e.target.value)}
                      style={{ border: "none", background: "transparent", outline: "none", fontSize: 10, color: T.text, width: "100%", minWidth: 30, textAlign: "center", fontFamily: "inherit", padding: 0 }}
                    />
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TableInspector({ T, table, updateTable, duplicateTable, deleteTable, spaceSeatsEvenly }) {
  const sInp = input(T);
  if (!table) {
    return <div style={{ ...card(T), color: T.textMuted }}>Select a table to edit its shape, size and seat names.</div>;
  }

  return (
    <div style={{ ...card(T), display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 20 }}>Selected Table</div>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" onClick={() => spaceSeatsEvenly(table.id)} title="Reset seats to even spacing around the table" style={{ ...btn2(T), padding: "8px 12px" }}>Space evenly</button>
          <button type="button" onClick={() => duplicateTable(table.id)} style={{ ...btn2(T), padding: "8px 12px" }}>Duplicate</button>
          <button type="button" onClick={() => deleteTable(table.id)} style={{ ...btn2(T), padding: "8px 12px", color: T.danger, borderColor: T.dangerBorder }}>Delete</button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <label style={label(T)}>Table label</label>
          <input value={table.label} onChange={(e) => updateTable(table.id, { label: e.target.value })} style={sInp} />
        </div>
        <div>
          <label style={label(T)}>Shape</label>
          <select value={table.shape} onChange={(e) => updateTable(table.id, { shape: e.target.value }, true)} style={sInp}>
            <option value="rect">Square / rectangular</option>
            <option value="round">Round</option>
          </select>
        </div>
        <div>
          <label style={label(T)}>Seats</label>
          <input type="number" min="1" max="24" value={table.seatCount} onChange={(e) => updateTable(table.id, { seatCount: Number(e.target.value) || 1 }, true)} style={sInp} />
        </div>
        <div>
          <label style={label(T)}>X position</label>
          <input type="number" value={table.x} onChange={(e) => updateTable(table.id, { x: Number(e.target.value) || 0 })} style={sInp} />
        </div>
        <div>
          <label style={label(T)}>Y position</label>
          <input type="number" value={table.y} onChange={(e) => updateTable(table.id, { y: Number(e.target.value) || 0 })} style={sInp} />
        </div>
        <div>
          <label style={label(T)}>{table.shape === "round" ? "Diameter" : "Width"}</label>
          <input type="number" min="80" max="1000" value={table.width} onChange={(e) => updateTable(table.id, { width: clamp(Number(e.target.value) || 80, 80, 1000), ...(table.shape === "round" ? { height: clamp(Number(e.target.value) || 80, 80, 1000) } : {}) })} style={sInp} />
        </div>
        <div>
          <label style={label(T)}>{table.shape === "round" ? "Diameter slider" : "Height"}</label>
          {table.shape === "round" ? (
            <input type="range" min="80" max="400" value={table.width} onChange={(e) => updateTable(table.id, { width: Number(e.target.value), height: Number(e.target.value) })} style={{ width: "100%" }} />
          ) : (
            <input type="number" min="80" max="1000" value={table.height} onChange={(e) => updateTable(table.id, { height: clamp(Number(e.target.value) || 80, 80, 1000) })} style={sInp} />
          )}
        </div>
      </div>

      <div style={{ fontSize: 12, color: T.textMuted }}>
        Seat labels can now be dragged around the edge of the table to fine-tune placement.
      </div>

      <div>
        <div style={{ ...label(T), marginBottom: 10 }}>Seat names</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, maxHeight: 360, overflow: "auto" }}>
          {table.seats.map((seat, idx) => (
            <div key={seat.id}>
              <label style={{ ...label(T), marginBottom: 4 }}>Seat {idx + 1}</label>
              <input
                value={seat.name}
                onChange={(e) => {
                  const nextSeats = table.seats.map((s, sIdx) => sIdx === idx ? { ...s, name: e.target.value } : s);
                  updateTable(table.id, { seats: nextSeats });
                }}
                placeholder={`Guest ${idx + 1}`}
                style={sInp}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function TablePlansModule({ lodgeTreasurerEmails = {}, bookings = [], lodges = [], user, notify, theme, activeLodgeNumber, activeLodgeName }) {
  const T = theme;
  const styles = { card: card(T), btn: btn(T), btn2: btn2(T), inp: input(T), lbl: label(T) };
  const isTreasurer = user?.role === "treasurer";
  const allowedBookings = useMemo(() => {
    const source = isTreasurer ? bookings.filter((b) => String(b.lodgeNumber) === String(activeLodgeNumber)) : bookings;
    return [...source].sort((a, b) => `${b.date || ""} ${b.time || ""}`.localeCompare(`${a.date || ""} ${a.time || ""}`));
  }, [bookings, isTreasurer, activeLodgeNumber]);

  const [bookingId, setBookingId] = useState(allowedBookings[0]?.id || "");
  const [plan, setPlan] = useState(emptyPlan());
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [copyFromId, setCopyFromId] = useState("");

  useEffect(() => {
    if (!allowedBookings.some((b) => b.id === bookingId)) {
      setBookingId(allowedBookings[0]?.id || "");
    }
  }, [allowedBookings, bookingId]);

  const booking = allowedBookings.find((b) => b.id === bookingId) || null;
  useEffect(() => { if (bookingId) void recordActivity("booking.tablePlan.view", { collection: "bookings", id: bookingId }); }, [bookingId]);
  const lodgeName = booking?.lodgeName || activeLodgeName || lodges.find((l) => String(l.number) === String(activeLodgeNumber))?.name || "";
  const lodgeLogo = (() => { const l = lodges.find((x) => String(x.number) === String((booking || {}).lodgeNumber)); return l && l.logo && String(l.logo).startsWith("data:image") ? l.logo : ""; })();
  const previousPlans = useMemo(() => {
    if (!booking) return [];
    return allowedBookings.filter((b) => b.id !== booking.id && String(b.lodgeNumber) === String(booking.lodgeNumber) && b.tablePlanLayout?.tables?.length);
  }, [allowedBookings, booking]);

  useEffect(() => {
    if (!booking) {
      setPlan(emptyPlan());
      setSelectedId(null);
      return;
    }
    const next = normalisePlan(booking.tablePlanLayout);
    setPlan(next);
    setSelectedId(next.tables[0]?.id || null);
    setCopyFromId("");
  }, [bookingId, booking?.tablePlanLayout]);

  const selectedTable = plan.tables.find((t) => t.id === selectedId) || null;

  const setPlanTables = (fn) => {
    setPlan((prev) => {
      const nextTables = fn(prev.tables);
      return { ...prev, tables: nextTables };
    });
  };

  const addTable = (shape) => {
    const table = normaliseTable({ shape, label: `${shape === "round" ? "Round" : "Table"} ${plan.tables.length + 1}` }, plan.tables.length);
    if (shape === "round") {
      table.width = 180;
      table.height = 180;
      table.seatCount = 8;
      table.seats = resizeSeats(table, 8, "round").seats;
    } else {
      table.width = 220;
      table.height = 110;
      table.seatCount = 6;
      table.seats = resizeSeats(table, 6, "rect").seats;
    }
    setPlanTables((tables) => [...tables, table]);
    setSelectedId(table.id);
  };

  const updateTable = (tableId, patch, resizeSeatArray = false) => {
    setPlanTables((tables) => tables.map((t) => {
      if (t.id !== tableId) return t;
      const nextShape = patch.shape || t.shape;
      let next = { ...t, ...patch, shape: nextShape };
      if (next.shape === "round") next.height = next.width;
      if (resizeSeatArray || patch.seatCount !== undefined || patch.shape !== undefined) next = resizeSeats(next, next.seatCount, nextShape);
      return next;
    }));
  };

  const updateTablePosition = (tableId, x, y) => {
    setPlanTables((tables) => tables.map((t) => t.id === tableId ? ({ ...t, x: clamp(Math.round(x), 0, Math.max(0, plan.room.width - t.width)), y: clamp(Math.round(y), 0, Math.max(0, plan.room.height - t.height)) }) : t));
  };

  const updateSeatName = (tableId, seatId, name) => {
    setPlanTables((tables) => tables.map((t) => t.id !== tableId ? t : ({ ...t, seats: t.seats.map((seat) => seat.id === seatId ? { ...seat, name } : seat) })));
  };
  const spaceSeatsEvenly = (tableId) => {
    setPlanTables((tables) => tables.map((t) => {
      if (t.id !== tableId) return t;
      const count = t.seats.length || 1;
      return { ...t, seats: t.seats.map((seat, i) => ({ ...seat, ...defaultSeatPlacement(t.shape, i, count) })) };
    }));
  };
  const updateSeatPlacement = (tableId, seatId, relX, relY) => {
    setPlanTables((tables) => tables.map((t) => {
      if (t.id !== tableId) return t;
      const w = Number(t.width) || 220;
      const h = Number(t.height) || 120;
      const seats = t.seats.map((seat) => {
        if (seat.id !== seatId) return seat;

        if (t.shape === "round") {
          const cx = w / 2;
          const cy = h / 2;
          const angle = (Math.atan2(relY - cy, relX - cx) * 180) / Math.PI;
          return { ...seat, angle };
        }

        const distTop = Math.abs(relY);
        const distRight = Math.abs(relX - w);
        const distBottom = Math.abs(relY - h);
        const distLeft = Math.abs(relX);
        const perim = 2 * (w + h);
        const nearest = Math.min(distTop, distRight, distBottom, distLeft);
        let d = 0;
        if (nearest === distTop) d = clamp(relX, 0, w);
        else if (nearest === distRight) d = w + clamp(relY, 0, h);
        else if (nearest === distBottom) d = w + h + (w - clamp(relX, 0, w));
        else d = 2 * w + h + (h - clamp(relY, 0, h));
        return { ...seat, t: clamp(d / perim, 0, 0.9999) };
      });
      return { ...t, seats };
    }));
  };

  const duplicateTable = (tableId) => {
    const source = plan.tables.find((t) => t.id === tableId);
    if (!source) return;
    const copy = normaliseTable({ ...deepClone(source), id: genId("table"), label: `${source.label} Copy`, x: source.x + 36, y: source.y + 36 }, plan.tables.length);
    setPlanTables((tables) => [...tables, copy]);
    setSelectedId(copy.id);
  };

  const deleteTable = (tableId) => {
    setPlanTables((tables) => tables.filter((t) => t.id !== tableId));
    if (selectedId === tableId) setSelectedId(null);
  };

  const clearPlan = () => {
    const next = emptyPlan();
    setPlan(next);
    setSelectedId(null);
  };

  const copyPrevious = () => {
    const source = previousPlans.find((b) => b.id === copyFromId);
    if (!source?.tablePlanLayout) return;
    const copied = normalisePlan(source.tablePlanLayout);
    setPlan(copied);
    setSelectedId(copied.tables[0]?.id || null);
    notify?.(`Copied table plan from ${fmtDate(source.date)}`);
  };

  const savePlan = async () => {
    if (!booking) {
      notify?.("Choose a booking first", "error");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...normalisePlan(plan),
        updatedAt: new Date().toISOString(),
        updatedBy: user?.email || user?.name || "",
      };
      await updateDoc(doc(db, "bookings", booking.id), {
        tablePlanLayout: payload,
        tablePlanUpdatedAt: serverTimestamp(),
        tablePlanUpdatedBy: user?.email || user?.name || "",
      });
      notify?.("Table plan saved");
    } catch (e) {
      console.error(e);
      notify?.("Failed to save table plan", "error");
    } finally {
      setSaving(false);
    }
  };

  const openPrintableDocument = () => {
    if (!booking) {
      notify?.("Choose a booking first", "error");
      return null;
    }
    const html = planDocumentHtml(plan, booking, lodgeName, lodgeLogo, lodgeTreasurerEmails[String(booking?.lodgeNumber)]);
    const printWindow = window.open("", "_blank", "width=1400,height=1000");
    if (!printWindow) {
      notify?.("Popup blocked. Allow popups to print the table plan.", "error");
      return null;
    }
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    return printWindow;
  };

  const printPlan = () => {
    void recordActivity("tablePlan.print",{collection:"bookings",id:booking?.id||""});
    const printWindow = openPrintableDocument();
    if (!printWindow) return;
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const downloadPlan = () => {
    void recordActivity("tablePlan.download",{collection:"bookings",id:booking?.id||""});
    if (!booking) {
      notify?.("Choose a booking first", "error");
      return;
    }
    const html = planDocumentHtml(plan, booking, lodgeName, lodgeLogo, lodgeTreasurerEmails[String(booking?.lodgeNumber)]);
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const safeLodge = String(booking.lodgeName || lodgeName || "table-plan").replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase();
    const safeDate = String(booking.date || "undated");
    a.href = url;
    a.download = `${safeLodge || "table-plan"}-${safeDate}.html`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ animation: "fadeUp .4s ease" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginBottom: 22 }}>
        <div>
          <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, margin: 0, color: T.text }}>Table Plans</h1>
          <p style={{ margin: "5px 0 0", color: T.textMuted, fontSize: 13 }}>
            Build and save seating plans against bookings. {isTreasurer ? lodgeName : "All lodges available."}
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button type="button" onClick={() => addTable("round")} style={styles.btn2}>+ Round table</button>
          <button type="button" onClick={() => addTable("rect")} style={styles.btn2}>+ Square / rectangular table</button>
          <button type="button" onClick={downloadPlan} style={styles.btn2} disabled={!booking}>Download</button>
          <button type="button" onClick={printPlan} style={styles.btn2} disabled={!booking}>Print</button>
          <button type="button" onClick={savePlan} style={styles.btn} disabled={saving || !booking}>{saving ? "Saving..." : "Save plan"}</button>
        </div>
      </div>

      <div style={{ ...styles.card, marginBottom: 18 }}>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1.2fr 1fr 1fr", gap: 14 }}>
          <div>
            <label style={styles.lbl}>Booking</label>
            <SearchableSelect aria-label="Table plan booking" value={bookingId} onChange={(e) => setBookingId(e.target.value)} style={styles.inp}>
              <option value="">Select booking</option>
              {allowedBookings.map((b) => (
                <option key={b.id} value={b.id}>{b.lodgeName} (#{b.lodgeNumber}) · {fmtDate(b.date)} · {b.time || ""} · {b.diners} covers</option>
              ))}
            </SearchableSelect>
          </div>
          <div>
            <label style={styles.lbl}>Copy previous plan</label>
            <SearchableSelect aria-label="Copy previous booking" value={copyFromId} onChange={(e) => setCopyFromId(e.target.value)} style={styles.inp} disabled={!booking || previousPlans.length === 0}>
              <option value="">Choose previous booking</option>
              {previousPlans.map((b) => (
                <option key={b.id} value={b.id}>{b.lodgeName} (#{b.lodgeNumber}) · {fmtDate(b.date)} · {b.time || ""} · {b.diners} covers</option>
              ))}
            </SearchableSelect>
          </div>
          <div>
            <label style={styles.lbl}>Room width</label>
            <input type="number" min="600" max="2400" value={plan.room.width} onChange={(e) => setPlan((p) => ({ ...p, room: { ...p.room, width: Number(e.target.value) || 1200 } }))} style={styles.inp} />
          </div>
          <div>
            <label style={styles.lbl}>Room height</label>
            <input type="number" min="400" max="1800" value={plan.room.height} onChange={(e) => setPlan((p) => ({ ...p, room: { ...p.room, height: Number(e.target.value) || 800 } }))} style={styles.inp} />
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ fontSize: 12, color: T.textMuted }}>{booking ? `Editing ${booking.lodgeName} booking on ${fmtDate(booking.date)}` : "Choose a booking to start"}</div>
          <button type="button" onClick={copyPrevious} disabled={!copyFromId} style={{ ...styles.btn2, padding: "8px 12px" }}>Copy selected plan</button>
          <button type="button" onClick={clearPlan} style={{ ...styles.btn2, padding: "8px 12px" }}>Clear plan</button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1.8fr 1fr", gap: 18, alignItems: "start" }}>
        <Board T={T} plan={plan} selectedId={selectedId} setSelectedId={setSelectedId} updateTablePosition={updateTablePosition} updateSeatPlacement={updateSeatPlacement} updateSeatName={updateSeatName} />
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <TableInspector T={T} table={selectedTable} updateTable={updateTable} duplicateTable={duplicateTable} deleteTable={deleteTable} spaceSeatsEvenly={spaceSeatsEvenly} />
          <div style={styles.card}>
            <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, marginBottom: 12 }}>Plan Notes</div>
            <textarea value={plan.notes} onChange={(e) => setPlan((p) => ({ ...p, notes: e.target.value }))} rows={7} style={{ ...styles.inp, resize: "vertical" }} placeholder="Notes for service, top table, dietary groupings, wheelchair access, etc." />
            {booking && (
              <div style={{ marginTop: 12, fontSize: 12, color: T.textMuted }}>
                Stored on booking ID <span style={{ fontWeight: 700, color: T.text }}>{booking.id}</span> and can be amended at any time.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
